import { toItem, type VaultItem } from '../library/vault/vaultData'

/** OnlyFans post / comment / user payloads → what the OnlyFans pages show (shapes: SHAPES.md 2026-10-08). */

type Raw = Record<string, unknown>

const num = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? v : 0)
const str = (v: unknown) => (typeof v === 'string' ? v : '')

export interface PostLabel {
  id: string
  name: string
}

export interface Post {
  id: number
  authorId: number
  postedAt: string
  /** Unix seconds with six decimals, as OnlyFans pages by it ("1791476072.000000"). */
  postedAtPrecise: string
  expiredAt: string | null
  /** OnlyFans HTML. */
  text: string
  media: VaultItem[]
  mediaCount: number
  /** USD for a paid post, 0 when free. */
  price: number
  /** Media ids free before purchase (OnlyFans `preview`). */
  previewIds: number[]
  /** Media this Account can't open yet (another creator's unbought PPV: `canView: false`, no files). */
  lockedIds: number[]
  likes: number
  comments: number
  tips: string
  isLiked: boolean
  isBookmarked: boolean
  isPinned: boolean
  canEdit: boolean
  canDelete: boolean
  canComment: boolean
  canLike: boolean
  /** Custom labels the post is in. */
  labels: PostLabel[]
}

function previewIdsFrom(raw: Raw, media: Raw[]): number[] {
  const list = Array.isArray(raw.preview) ? raw.preview : Array.isArray(raw.previews) ? raw.previews : []
  const ids = list
    .map((x) => (typeof x === 'object' && x ? num((x as Raw).id) : num(x)))
    .filter((n) => n > 0)
  if (ids.length) return ids
  return media.filter((m) => m.isFree === true || m.isPreview === true || m.hasCustomPreview === true).map((m) => num(m.id)).filter((n) => n > 0)
}

export function toPost(raw: Raw): Post {
  const media = Array.isArray(raw.media) ? (raw.media as Raw[]) : []
  const labels = Array.isArray(raw.labelStates) ? (raw.labelStates as Raw[]) : []
  return {
    id: num(raw.id),
    authorId: num((raw.author as Raw | undefined)?.id),
    postedAt: str(raw.postedAt),
    postedAtPrecise: str(raw.postedAtPrecise),
    expiredAt: str(raw.expiredAt) || null,
    text: str(raw.text),
    media: media.map(toItem),
    mediaCount: num(raw.mediaCount) || media.length,
    price: num(raw.price),
    previewIds: previewIdsFrom(raw, media),
    lockedIds: media.filter((m) => m.canView === false).map((m) => num(m.id)).filter((n) => n > 0),
    likes: num(raw.favoritesCount),
    comments: num(raw.commentsCount),
    tips: str(raw.tipsAmount),
    isLiked: raw.isFavorite === true,
    isBookmarked: raw.isBookmarked === true,
    isPinned: raw.isPinned === true,
    canEdit: raw.canEdit === true,
    canDelete: raw.canDelete === true,
    canComment: raw.canComment === true,
    canLike: raw.canToggleFavorite === true,
    labels: labels.filter((l) => l.type === 'custom').map((l) => ({ id: String(l.id), name: str(l.name) })),
  }
}

/** A post OnlyFans lets us edit with the text-only form (no media, no price). */
export function isTextOnly(p: Post): boolean {
  return p.mediaCount === 0 && p.price === 0
}

export interface OfUser {
  id: number
  name: string
  username: string
  avatar: string
  verified: boolean
}

function thumb(raw: Raw, field: string, thumbs: string, sizes: string[]): string {
  const set = (raw[thumbs] ?? {}) as Raw
  for (const s of sizes) if (str(set[s])) return str(set[s])
  return str(raw[field])
}

export function toUser(raw: Raw): OfUser {
  return {
    id: num(raw.id),
    name: str(raw.displayName) || str(raw.name),
    username: str(raw.username),
    avatar: thumb(raw, 'avatar', 'avatarThumbs', ['c144', 'c50']),
    verified: raw.isVerified === true,
  }
}

export interface Comment {
  id: number
  authorId: number
  text: string
  postedAt: string
  likes: number
  isLiked: boolean
  isPinned: boolean
  canLike: boolean
}

export function toComment(raw: Raw): Comment {
  return {
    id: num(raw.id),
    authorId: num((raw.author as Raw | undefined)?.id),
    text: str(raw.text),
    postedAt: str(raw.postedAt),
    likes: num(raw.likesCount),
    isLiked: raw.isLiked === true,
    isPinned: raw.isPinned === true,
    canLike: raw.canLike !== false,
  }
}

export interface PostStats {
  views: number
  uniqueViews: number
  likes: number
  comments: number
  tipCount: number
  tipSum: number
  purchases: number
  purchasesSum: number
  /** Seconds. */
  duration: number
  durationAverage: number
}

export function toStats(raw: Raw): PostStats {
  return {
    views: num(raw.lookCount),
    uniqueViews: num(raw.uniqueLookCount),
    likes: num(raw.likeCount),
    comments: num(raw.commentCount),
    tipCount: num(raw.tipCount),
    tipSum: num(raw.tipSum),
    purchases: num(raw.purchasedCount),
    purchasesSum: num(raw.purchasedSumm),
    duration: num(raw.lookDuration),
    durationAverage: num(raw.lookDurationAverage),
  }
}

/** hh:mm:ss, the way OnlyFans' post statistics shows durations. */
export function hms(seconds: number): string {
  const s = Math.max(0, Math.round(seconds))
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${pad(Math.floor(s / 3600))}:${pad(Math.floor((s % 3600) / 60))}:${pad(s % 60)}`
}

/** 29067 → "29.1K", the way OnlyFans' profile header shows counts. */
export function compact(n: number): string {
  if (n >= 1_000_000) return `${+(n / 1_000_000).toFixed(1)}M`
  if (n >= 1_000) return `${+(n / 1_000).toFixed(1)}K`
  return String(n)
}

/** End of a local calendar day as OnlyFans' beforePublishTime ("…59.999999"). */
export function endOfDayMarker(date: string): string {
  const [y = 0, m = 1, d = 1] = date.split('-').map(Number)
  const end = new Date(y, m - 1, d, 23, 59, 59)
  return `${Math.floor(end.getTime() / 1000)}.999999`
}
